import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Spark } from "@/components/Doodles";
import { Trash2, Pencil } from "lucide-react";

const LoginForm = () => {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        const res = await login(email, password);
        if (!res.ok) setError(res.error);
        setBusy(false);
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-forest px-5">
            <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border-2 border-ink bg-cream p-8 hard-shadow-lg -rotate-1">
                <Spark className="h-8 w-8 text-orange" />
                <h1 className="mt-2 font-display text-4xl tracking-wide text-ink">CLUBHOUSE OFFICE</h1>
                <p className="mt-1 text-sm text-ink/70">Admin only. The corkboard polices itself otherwise.</p>
                <label className="mt-6 block text-sm font-semibold text-ink" htmlFor="admin-email">Email</label>
                <input
                    id="admin-email"
                    data-testid="admin-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full border-2 border-ink bg-paper px-4 py-3 text-ink"
                />
                <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="admin-password">Password</label>
                <input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full border-2 border-ink bg-paper px-4 py-3 text-ink"
                />
                {error && (
                    <p data-testid="admin-login-error" className="mt-3 font-semibold text-brick">{error}</p>
                )}
                <button data-testid="admin-login-submit" type="submit" disabled={busy} className="btn-stamp mt-6 w-full disabled:opacity-60">
                    {busy ? "Checking the list…" : "Let Me In"}
                </button>
            </form>
        </div>
    );
};

const CopyTab = () => {
    const [content, setContent] = useState(null);
    const [drafts, setDrafts] = useState({});

    useEffect(() => {
        api.get("/content").then((r) => {
            setContent(r.data);
            const d = {};
            Object.entries(r.data).forEach(([k, v]) => {
                d[k] = typeof v === "string" ? v : JSON.stringify(v, null, 2);
            });
            setDrafts(d);
        });
    }, []);

    if (!content) return <p className="font-hand text-2xl text-ink/60">Shuffling papers…</p>;

    const groups = {};
    Object.keys(content).sort().forEach((k) => {
        const g = k.split(".")[0];
        (groups[g] = groups[g] || []).push(k);
    });

    const save = async (key) => {
        let value = drafts[key];
        if (typeof content[key] !== "string") {
            try {
                value = JSON.parse(drafts[key]);
            } catch {
                toast.error(`"${key}" isn't valid JSON. Fix the brackets, boss.`);
                return;
            }
        }
        try {
            await api.put(`/content/${key}`, { value });
            toast.success(`Saved ${key}`);
            setContent({ ...content, [key]: value });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-10" data-testid="admin-copy-tab">
            {Object.entries(groups).map(([group, keys]) => (
                <section key={group}>
                    <h2 className="font-display text-2xl tracking-wider text-forest">{group.toUpperCase()}</h2>
                    <div className="mt-4 space-y-4">
                        {keys.map((key) => (
                            <div key={key} className="border-2 border-ink bg-paper p-4 hard-shadow">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <label className="font-mono text-xs font-bold text-ink/70" htmlFor={`copy-${key}`}>{key}</label>
                                    <button
                                        data-testid={`copy-save-${key.replace(/[^a-z0-9]/gi, "-")}`}
                                        onClick={() => save(key)}
                                        className="border-2 border-ink bg-mustard px-3 py-1 font-display text-sm tracking-wider text-ink hard-shadow transition-transform duration-150 hover:-translate-y-0.5"
                                    >
                                        Save
                                    </button>
                                </div>
                                <textarea
                                    id={`copy-${key}`}
                                    data-testid={`copy-field-${key.replace(/[^a-z0-9]/gi, "-")}`}
                                    rows={typeof content[key] === "string" ? 3 : 10}
                                    value={drafts[key] ?? ""}
                                    onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                                    className="mt-2 w-full border-2 border-ink bg-cream px-3 py-2 font-mono text-sm text-ink"
                                />
                                {typeof content[key] !== "string" && (
                                    <p className="mt-1 text-xs text-ink/50">Structured content — edit as JSON, keep the shape.</p>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
};

const EMPTY_EVENT = { title: "", date: "", time: "", location: "The Clubhouse", tag: "Gathering", description: "", rsvp_url: "" };

const PaymentsTab = () => {
    const [payments, setPayments] = useState(null);

    useEffect(() => {
        api.get("/payments")
            .then((r) => setPayments(r.data))
            .catch(() => setPayments([]));
    }, []);

    const paidTotal = (payments || []).filter((p) => p.payment_status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0);

    return (
        <div data-testid="admin-payments-tab">
            <p data-testid="payments-total" className="font-display text-2xl tracking-wider text-forest">
                {payments ? `$${paidTotal.toFixed(2)} IN DUES SO FAR` : "Counting the jar…"}
            </p>
            <div className="mt-6 overflow-x-auto border-2 border-ink hard-shadow">
                <table className="w-full min-w-[640px] bg-paper text-left text-sm">
                    <thead>
                        <tr className="border-b-2 border-ink bg-mustard font-display tracking-wider">
                            <th className="px-4 py-3">Tier</th>
                            <th className="px-4 py-3">Amount</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(payments || []).map((p) => (
                            <tr key={p.session_id} data-testid={`payment-row-${p.session_id}`} className="border-b border-ink/20">
                                <td className="px-4 py-3 font-medium">{p.tier_name || p.lookup_key}</td>
                                <td className="px-4 py-3">${Number(p.amount || 0).toFixed(2)} {String(p.currency || "").toUpperCase()}</td>
                                <td className="px-4 py-3">
                                    <span className={`inline-block border-2 border-ink px-2 py-0.5 font-display text-xs tracking-[0.12em] ${
                                        p.payment_status === "paid" ? "bg-forest text-cream" : p.payment_status === "pending" ? "bg-mustard text-ink" : "bg-brick text-cream"
                                    }`}>
                                        {(p.payment_status || "unknown").toUpperCase()}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{p.created_at ? new Date(p.created_at).toLocaleString() : ""}</td>
                            </tr>
                        ))}
                        {payments && payments.length === 0 && (
                            <tr><td colSpan={4} className="px-4 py-8 text-center font-hand text-2xl text-ink/60">No dues yet. The jar is empty but optimistic.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const STUDIO_HOURS = Array.from({ length: 17 }, (_, i) => `${String(i + 7).padStart(2, "0")}:00`);

const StudioTab = () => {
    const [bookings, setBookings] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [blockForm, setBlockForm] = useState({ date: "", start: "07:00", hours: 17, note: "" });
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [manualForm, setManualForm] = useState({ name: "", email: "", date: "", start: "10:00", hours: 1, purpose: "", complimentary: true });

    const load = () => api.get("/studio/bookings/all").then((r) => setBookings(r.data));
    const loadBlocks = () => api.get("/studio/blocks").then((r) => setBlocks(r.data)).catch(() => {});
    const loadSettings = () => api.get("/studio/settings").then((r) => setSettings(r.data)).catch(() => {});
    const loadStats = () => api.get("/studio/stats").then((r) => setStats(r.data)).catch(() => {});
    useEffect(() => {
        load();
        loadBlocks();
        loadSettings();
        loadStats();
    }, []);

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            await api.put("/studio/settings", {
                ...settings,
                capacity: Number(settings.capacity),
                public_rate_cents: Math.round(Number(settings.public_rate_cents)),
                min_hours: Number(settings.min_hours),
                max_hours: Number(settings.max_hours),
                open_hour: Number(settings.open_hour),
                close_hour: Number(settings.close_hour),
                advance_days: Number(settings.advance_days),
                cancel_hours: Number(settings.cancel_hours),
                hold_minutes: Number(settings.hold_minutes),
                tier_rates: Object.fromEntries(Object.entries(settings.tier_rates).map(([k, v]) => [k, Math.round(Number(v))])),
                included_hours: Object.fromEntries(Object.entries(settings.included_hours).map(([k, v]) => [k, Number(v)])),
            });
            toast.success("Work room rules updated — they apply everywhere immediately.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const manualCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post("/studio/bookings/admin", { ...manualForm, hours: Number(manualForm.hours) });
            toast.success("Reservation created and confirmation emailed.");
            setManualForm({ name: "", email: "", date: "", start: "10:00", hours: 1, purpose: "", complimentary: true });
            load();
            loadStats();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const refund = async (id) => {
        try {
            await api.post(`/studio/bookings/${id}/refund`);
            toast.success("Refund issued through Stripe.");
            load();
            loadStats();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        await api.delete(`/studio/bookings/${id}`);
        toast.success("Reservation cancelled.");
        load();
        loadStats();
    };

    const startEdit = (b) => {
        setEditing(b.id);
        setEditForm({ date: b.date, start: b.start, hours: b.hours, purpose: b.purpose || "", status: b.status });
    };

    const saveEdit = async (id) => {
        try {
            await api.put(`/studio/bookings/${id}`, { ...editForm, hours: Number(editForm.hours) });
            toast.success("Reservation updated.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const addBlock = async (e) => {
        e.preventDefault();
        try {
            await api.post("/studio/blocks", { ...blockForm, hours: Number(blockForm.hours) });
            toast.success("Work room blocked off.");
            setBlockForm({ date: "", start: "07:00", hours: 17, note: "" });
            loadBlocks();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const removeBlock = async (id) => {
        await api.delete(`/studio/blocks/${id}`);
        toast.success("Block lifted.");
        loadBlocks();
    };

    const miniInput = "border-2 border-ink bg-cream px-2 py-1.5 text-ink text-sm";
    const miniBtn = "border-2 border-ink px-3 py-1.5 font-display text-sm tracking-wider hard-shadow";

    return (
        <div data-testid="admin-studio-tab" className="space-y-10">
            {stats && (
                <div data-testid="workroom-stats" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {[["Revenue", `$${stats.revenue.toFixed(2)}`], ["Reservations", stats.confirmed_total], ["Members / Guests", `${stats.member_bookings} / ${stats.guest_bookings}`], ["Utilization (14d)", `${stats.utilization_pct}%`]].map(([label, value]) => (
                        <div key={label} className="border-2 border-ink bg-paper p-4 hard-shadow">
                            <p className="font-display text-2xl tracking-wide text-ink">{value}</p>
                            <p className="text-xs font-semibold text-ink/60">{label}</p>
                        </div>
                    ))}
                </div>
            )}

            {settings && (
                <form onSubmit={saveSettings} data-testid="workroom-settings-form" className="border-2 border-ink bg-paper p-5 hard-shadow-lg">
                    <h3 className="font-display text-2xl tracking-wider text-ink">ROOM RULES + RATES</h3>
                    <p className="mt-1 text-sm text-ink/60">Changes apply instantly to new bookings, the public rate card, and availability rules.</p>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <div>
                            <label className="block text-xs font-semibold text-ink">Public rate (¢/hr)</label>
                            <input type="number" data-testid="set-public-rate" value={settings.public_rate_cents} onChange={(e) => setSettings({ ...settings, public_rate_cents: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Max simultaneous reservations (1 = exclusive whole-room)</label>
                            <input type="number" min="1" max="10" data-testid="set-capacity" value={settings.capacity} onChange={(e) => setSettings({ ...settings, capacity: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Min hours</label>
                            <input type="number" min="1" value={settings.min_hours} onChange={(e) => setSettings({ ...settings, min_hours: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Max hours</label>
                            <input type="number" min="1" max="17" value={settings.max_hours} onChange={(e) => setSettings({ ...settings, max_hours: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Opens (hour)</label>
                            <input type="number" min="0" max="23" value={settings.open_hour} onChange={(e) => setSettings({ ...settings, open_hour: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Closes (24 = midnight)</label>
                            <input type="number" min="1" max="24" value={settings.close_hour} onChange={(e) => setSettings({ ...settings, close_hour: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Advance window (days)</label>
                            <input type="number" min="1" value={settings.advance_days} onChange={(e) => setSettings({ ...settings, advance_days: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Cancel notice (hours)</label>
                            <input type="number" min="0" value={settings.cancel_hours} onChange={(e) => setSettings({ ...settings, cancel_hours: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {["regular", "weirdo", "founding"].map((t) => (
                            <div key={t}>
                                <label className="block text-xs font-semibold text-ink">{t.charAt(0).toUpperCase() + t.slice(1)} rate (¢/hr)</label>
                                <input type="number" data-testid={`set-rate-${t}`} value={settings.tier_rates?.[t] ?? ""} onChange={(e) => setSettings({ ...settings, tier_rates: { ...settings.tier_rates, [t]: e.target.value } })} className={`${miniInput} w-full`} />
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        <div>
                            <label className="block text-xs font-semibold text-ink">Founding included hrs/mo</label>
                            <input type="number" min="0" data-testid="set-included-founding" value={settings.included_hours?.founding ?? 0} onChange={(e) => setSettings({ ...settings, included_hours: { ...settings.included_hours, founding: e.target.value } })} className={`${miniInput} w-full`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-ink">Checkout hold (min)</label>
                            <input type="number" min="5" value={settings.hold_minutes} onChange={(e) => setSettings({ ...settings, hold_minutes: e.target.value })} className={`${miniInput} w-full`} />
                        </div>
                        <label className="flex items-end gap-2 pb-1 text-xs font-semibold text-ink">
                            <input type="checkbox" data-testid="set-refund-on-cancel" checked={!!settings.refund_on_cancel} onChange={(e) => setSettings({ ...settings, refund_on_cancel: e.target.checked })} className="h-4 w-4 border-2 border-ink" />
                            Auto-refund eligible cancellations
                        </label>
                    </div>
                    <button type="submit" data-testid="settings-save-button" className={`${miniBtn} mt-4 bg-forest text-cream`}>Save Rules</button>
                </form>
            )}

            <div>
                <h3 className="font-display text-2xl tracking-wider text-ink">ADD A RESERVATION MANUALLY</h3>
                <p className="mt-1 text-sm text-ink/60">Walk-ins, comped hours, phone requests — the confirmation email goes out automatically.</p>
                <form onSubmit={manualCreate} data-testid="manual-booking-form" className="mt-4 flex flex-wrap items-end gap-3 border-2 border-ink bg-paper p-4 hard-shadow">
                    <div>
                        <label className="block text-xs font-semibold text-ink">Name</label>
                        <input required data-testid="manual-name" value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} className={miniInput} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-ink">Email</label>
                        <input required type="email" data-testid="manual-email" value={manualForm.email} onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })} className={miniInput} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-ink">Date</label>
                        <input required type="date" data-testid="manual-date" value={manualForm.date} onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })} className={miniInput} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-ink">Start</label>
                        <select value={manualForm.start} onChange={(e) => setManualForm({ ...manualForm, start: e.target.value })} className={miniInput}>
                            {STUDIO_HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-ink">Hours</label>
                        <select value={manualForm.hours} onChange={(e) => setManualForm({ ...manualForm, hours: e.target.value })} className={miniInput}>
                            {[1, 2, 3].map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                    <label className="flex items-center gap-2 pb-1 text-xs font-semibold text-ink">
                        <input type="checkbox" data-testid="manual-complimentary" checked={manualForm.complimentary} onChange={(e) => setManualForm({ ...manualForm, complimentary: e.target.checked })} className="h-4 w-4 border-2 border-ink" />
                        Complimentary
                    </label>
                    <button type="submit" data-testid="manual-booking-submit" className={`${miniBtn} bg-orange text-cream`}>Book It</button>
                </form>
            </div>

            <div>
                <h3 className="font-display text-2xl tracking-wider text-ink">BLOCK OFF TIME</h3>
                <p className="mt-1 text-sm text-ink/60">Maintenance, deep cleans, private clubhouse use — blocked stretches reject reservations automatically.</p>
                <form onSubmit={addBlock} data-testid="block-form" className="mt-4 flex flex-wrap items-end gap-3 border-2 border-ink bg-paper p-4 hard-shadow">
                    <div>
                        <label className="block text-xs font-semibold text-ink" htmlFor="block-date">Date</label>
                        <input id="block-date" data-testid="block-date-input" type="date" required value={blockForm.date}
                            onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} className={miniInput} />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-ink" htmlFor="block-start">From</label>
                        <select id="block-start" data-testid="block-start-select" value={blockForm.start}
                            onChange={(e) => setBlockForm({ ...blockForm, start: e.target.value })} className={miniInput}>
                            {STUDIO_HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-ink" htmlFor="block-hours">Hours</label>
                        <select id="block-hours" data-testid="block-hours-select" value={blockForm.hours}
                            onChange={(e) => setBlockForm({ ...blockForm, hours: e.target.value })} className={miniInput}>
                            {Array.from({ length: 17 }, (_, i) => i + 1).map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                    <div className="min-w-[180px] flex-1">
                        <label className="block text-xs font-semibold text-ink" htmlFor="block-note">Note (internal)</label>
                        <input id="block-note" data-testid="block-note-input" value={blockForm.note}
                            onChange={(e) => setBlockForm({ ...blockForm, note: e.target.value })} placeholder="waxing the panther…" className={`${miniInput} w-full`} />
                    </div>
                    <button type="submit" data-testid="block-add-button" className={`${miniBtn} bg-brick text-cream`}>Block It</button>
                </form>
                {blocks.length > 0 && (
                    <ul className="mt-4 space-y-2">
                        {blocks.map((b) => (
                            <li key={b.id} data-testid={`block-row-${b.id}`} className="flex items-center justify-between gap-3 border-2 border-ink bg-paper px-4 py-2 text-sm hard-shadow">
                                <span><strong>{b.date}</strong> · {b.start} · {b.hours} hr{b.hours > 1 ? "s" : ""}{b.note ? ` · ${b.note}` : ""}</span>
                                <button data-testid={`block-delete-${b.id}`} onClick={() => removeBlock(b.id)} aria-label="Remove block" className="border-2 border-ink bg-brick p-1 text-cream">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div>
                <h3 className="font-display text-2xl tracking-wider text-ink">RESERVATIONS</h3>
                <div className="mt-4 overflow-x-auto border-2 border-ink hard-shadow">
                    <table className="w-full min-w-[640px] bg-paper text-left text-sm">
                        <thead>
                            <tr className="border-b-2 border-ink bg-mustard font-display tracking-wider">
                                <th className="px-4 py-3">When</th>
                                <th className="px-4 py-3">Who</th>
                                <th className="px-4 py-3">Purpose</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3" aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {(bookings || []).map((b) => (
                                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-ink/20">
                                    {editing === b.id ? (
                                        <td colSpan={5} className="px-4 py-3">
                                            <div className="flex flex-wrap items-end gap-2">
                                                <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className={miniInput} data-testid={`edit-date-${b.id}`} />
                                                <select value={editForm.start} onChange={(e) => setEditForm({ ...editForm, start: e.target.value })} className={miniInput} data-testid={`edit-start-${b.id}`}>
                                                    {STUDIO_HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                                                </select>
                                                <select value={editForm.hours} onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })} className={miniInput}>
                                                    {[1, 2, 3].map((h) => <option key={h} value={h}>{h} hr</option>)}
                                                </select>
                                                <input value={editForm.purpose} onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })} placeholder="purpose" className={miniInput} />
                                                <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={miniInput} data-testid={`edit-status-${b.id}`}>
                                                    {["confirmed", "pending_payment", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <button onClick={() => saveEdit(b.id)} data-testid={`edit-save-${b.id}`} className={`${miniBtn} bg-forest text-cream`}>Save</button>
                                                <button onClick={() => setEditing(null)} className={`${miniBtn} bg-cream`}>Never Mind</button>
                                            </div>
                                        </td>
                                    ) : (
                                        <>
                                            <td className="px-4 py-3 font-medium">{b.date} · {b.start} · {b.hours} hr{b.hours > 1 ? "s" : ""}</td>
                                            <td className="px-4 py-3">{b.user_name} <span className="text-ink/50">({b.user_email})</span>{b.guest ? <span className="ml-1 border border-ink px-1 font-display text-[10px] tracking-wider text-ink/60">GUEST</span> : null}</td>
                                            <td className="px-4 py-3">{b.purpose || "—"}</td>
                                            <td className="px-4 py-3">{b.status === "confirmed" ? <span className="text-forest font-semibold">confirmed{b.refunded ? " (refunded)" : ""}</span> : <span className="text-brick">{b.status.replace(/_/g, " ")}</span>}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {b.amount > 0 && !b.refunded && b.status === "confirmed" && (
                                                        <button data-testid={`booking-refund-${b.id}`} onClick={() => refund(b.id)} aria-label="Refund reservation" title="Refund via Stripe"
                                                            className="border-2 border-ink bg-orange px-2 py-1.5 font-display text-xs tracking-wider text-cream hard-shadow">
                                                            REFUND
                                                        </button>
                                                    )}
                                                    <button data-testid={`booking-edit-${b.id}`} onClick={() => startEdit(b)} aria-label="Edit reservation" className="border-2 border-ink bg-mustard p-1.5 text-ink">
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    <button data-testid={`booking-delete-${b.id}`} onClick={() => remove(b.id)} aria-label="Cancel reservation" className="border-2 border-ink bg-brick p-1.5 text-cream">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            {bookings && bookings.length === 0 && (
                                <tr><td colSpan={5} className="px-4 py-8 text-center font-hand text-2xl text-ink/60">No reservations. The studio echoes.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const VolunteersTab = () => {
    const [volunteers, setVolunteers] = useState(null);

    const load = () => api.get("/volunteers").then((r) => setVolunteers(r.data));
    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        await api.delete(`/volunteers/${id}`);
        toast.success("Removed from the volunteer list.");
        load();
    };

    return (
        <div data-testid="admin-volunteers-tab">
            <p data-testid="volunteers-count" className="font-display text-2xl tracking-wider text-forest">
                {volunteers ? `${volunteers.length} HAND${volunteers.length === 1 ? "" : "S"} RAISED` : "Counting hands…"}
            </p>
            <div className="mt-6 overflow-x-auto border-2 border-ink hard-shadow">
                <table className="w-full min-w-[720px] bg-paper text-left text-sm">
                    <thead>
                        <tr className="border-b-2 border-ink bg-mustard font-display tracking-wider">
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Skills</th>
                            <th className="px-4 py-3">Availability</th>
                            <th className="px-4 py-3">Note</th>
                            <th className="px-4 py-3" aria-label="Actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {(volunteers || []).map((v) => (
                            <tr key={v.id} data-testid={`volunteer-row-${v.id}`} className="border-b border-ink/20">
                                <td className="px-4 py-3 font-medium">{v.name}</td>
                                <td className="px-4 py-3">{v.email}</td>
                                <td className="px-4 py-3">{v.skills || "—"}</td>
                                <td className="px-4 py-3">{v.availability || "—"}</td>
                                <td className="px-4 py-3">{v.message || "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    <button data-testid={`volunteer-delete-${v.id}`} onClick={() => remove(v.id)} aria-label={`Remove ${v.name}`} className="border-2 border-ink bg-brick p-1.5 text-cream">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {volunteers && volunteers.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center font-hand text-2xl text-ink/60">No volunteers yet. The hammer awaits.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const KIND_BADGES = { host: "bg-orange", calendar: "bg-forest", residency: "bg-brick" };
const KIND_LABELS = { host: "EVENT / HOST", calendar: "CALENDAR", residency: "RESIDENCY" };

const SubmissionsTab = () => {
    const [subs, setSubs] = useState(null);

    const load = () => api.get("/submissions").then((r) => setSubs(r.data));
    useEffect(() => {
        load();
    }, []);

    const mark = async (id, status) => {
        await api.put(`/submissions/${id}`, { status });
        toast.success(status === "approved" ? "Marked approved — now pin it to the Google Calendar." : "Marked reviewed.");
        load();
    };

    const remove = async (id) => {
        await api.delete(`/submissions/${id}`);
        toast.success("Submission tossed.");
        load();
    };

    return (
        <div data-testid="admin-submissions-tab">
            <p className="max-w-2xl text-sm text-ink/70">
                Event pitches, calendar submissions, and residency applications land here. Approve what's good, then add it to the
                Google Calendar — the calendar is the boss of the public board.
            </p>
            <div className="mt-6 space-y-4">
                {(subs || []).map((s) => (
                    <div key={s.id} data-testid={`submission-row-${s.id}`} className="border-2 border-ink bg-paper p-5 hard-shadow">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className={`border-2 border-ink px-2 py-0.5 font-display text-xs tracking-[0.15em] text-cream ${KIND_BADGES[s.kind] || "bg-ink"}`}>
                                {KIND_LABELS[s.kind] || s.kind.toUpperCase()}
                            </span>
                            <p className="font-display text-lg tracking-wide text-ink">{s.title || s.name}</p>
                            <span className={`font-display text-xs tracking-wider ${s.status === "approved" ? "text-forest" : s.status === "reviewed" ? "text-orange" : "text-ink/50"}`}>
                                {s.status.toUpperCase()}
                            </span>
                        </div>
                        <p className="mt-2 text-sm text-ink/75">
                            <strong>{s.name}</strong> · {s.email}
                            {s.timing ? ` · timing: ${s.timing}` : ""}
                            {s.attendance ? ` · crowd: ${s.attendance}` : ""}
                            {s.equipment ? ` · needs: ${s.equipment}` : ""}
                        </p>
                        {s.details ? <p className="mt-2 text-sm text-ink/70">{s.details}</p> : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                            {s.status === "new" && (
                                <button onClick={() => mark(s.id, "reviewed")} data-testid={`submission-review-${s.id}`} className="border-2 border-ink bg-cream px-3 py-1 font-display text-sm tracking-wider hard-shadow">
                                    Mark Reviewed
                                </button>
                            )}
                            {s.status !== "approved" && (
                                <button onClick={() => mark(s.id, "approved")} data-testid={`submission-approve-${s.id}`} className="border-2 border-ink bg-forest px-3 py-1 font-display text-sm tracking-wider text-cream hard-shadow">
                                    Approve
                                </button>
                            )}
                            <button onClick={() => remove(s.id)} data-testid={`submission-delete-${s.id}`} className="border-2 border-ink bg-brick px-3 py-1 font-display text-sm tracking-wider text-cream hard-shadow">
                                Toss
                            </button>
                        </div>
                    </div>
                ))}
                {subs && subs.length === 0 && (
                    <p className="border-2 border-dashed border-ink/40 bg-paper p-8 text-center font-hand text-2xl text-ink/60">
                        No pitches in the box. The corkboard waits.
                    </p>
                )}
            </div>
        </div>
    );
};

const EMPTY_REPORT = { month_label: "", income_lines: "", expense_lines: "", reserves: "", reinvestment: "", programs: "", note: "", published: false };

const ReportsTab = () => {
    const [reports, setReports] = useState(null);
    const [form, setForm] = useState(EMPTY_REPORT);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/transparency/all").then((r) => setReports(r.data));
    useEffect(() => {
        load();
    }, []);

    const toLines = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean);
    const fromLines = (arr) => (arr || []).join("\n");

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        const payload = {
            month_label: form.month_label,
            income_lines: toLines(form.income_lines),
            expense_lines: toLines(form.expense_lines),
            reserves: form.reserves,
            reinvestment: form.reinvestment,
            programs: form.programs,
            note: form.note,
            published: form.published,
        };
        try {
            if (editing) {
                await api.put(`/transparency/${editing}`, payload);
                toast.success("Report updated.");
            } else {
                await api.post("/transparency", payload);
                toast.success("Report pinned to the books.");
            }
            setForm(EMPTY_REPORT);
            setEditing(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    const startEdit = (r) => {
        setEditing(r.id);
        setForm({
            month_label: r.month_label,
            income_lines: fromLines(r.income_lines),
            expense_lines: fromLines(r.expense_lines),
            reserves: r.reserves || "",
            reinvestment: r.reinvestment || "",
            programs: r.programs || "",
            note: r.note || "",
            published: r.published,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (id) => {
        await api.delete(`/transparency/${id}`);
        toast.success("Report shredded.");
        load();
    };

    const taCls = "mt-1 w-full border-2 border-ink bg-cream px-3 py-2 text-sm text-ink placeholder:text-ink/40";

    return (
        <div data-testid="admin-reports-tab">
            <p className="max-w-2xl text-sm text-ink/70">
                Monthly money summaries, one line per entry ("Membership dues — $610"). Published reports appear on the public
                Transparency page with a CSV download. Nothing here is live accounting — just the plain-language version.
            </p>
            <form onSubmit={save} data-testid="report-form" className="mt-6 border-2 border-ink bg-paper p-6 hard-shadow-lg">
                <h3 className="font-display text-2xl tracking-wider text-ink">{editing ? "EDIT REPORT" : "NEW MONTHLY REPORT"}</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-semibold text-ink" htmlFor="report-month">Month</label>
                        <input id="report-month" data-testid="report-month-input" required value={form.month_label}
                            onChange={(e) => setForm({ ...form, month_label: e.target.value })} placeholder="November 2026"
                            className="mt-1 w-full border-2 border-ink bg-cream px-3 py-2 text-ink placeholder:text-ink/40" />
                    </div>
                    <label className="flex items-end gap-2 pb-1 text-sm font-semibold text-ink">
                        <input type="checkbox" data-testid="report-published-check" checked={form.published}
                            onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-5 w-5 border-2 border-ink" />
                        Published (visible on the site)
                    </label>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                        <label className="block text-sm font-semibold text-ink" htmlFor="report-income">Income — one per line</label>
                        <textarea id="report-income" data-testid="report-income-input" rows={4} value={form.income_lines}
                            onChange={(e) => setForm({ ...form, income_lines: e.target.value })}
                            placeholder={"Membership dues — $610\nStudio reservations — $150"} className={taCls} />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-ink" htmlFor="report-expenses">Expenses — one per line</label>
                        <textarea id="report-expenses" data-testid="report-expenses-input" rows={4} value={form.expense_lines}
                            onChange={(e) => setForm({ ...form, expense_lines: e.target.value })}
                            placeholder={"Rent — $1,200\nKava & coffee — $180"} className={taCls} />
                    </div>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    {[["reserves", "Reserves"], ["reinvestment", "Community reinvestment"], ["programs", "Program funding"]].map(([k, label]) => (
                        <div key={k}>
                            <label className="block text-sm font-semibold text-ink" htmlFor={`report-${k}`}>{label}</label>
                            <input id={`report-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                className="mt-1 w-full border-2 border-ink bg-cream px-3 py-2 text-sm text-ink" />
                        </div>
                    ))}
                </div>
                <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="report-note">Note (optional)</label>
                <input id="report-note" data-testid="report-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Big month — the soft opening bought a lot of lightbulbs." className="mt-1 w-full border-2 border-ink bg-cream px-3 py-2 text-sm text-ink placeholder:text-ink/40" />
                <div className="mt-5 flex gap-3">
                    <button type="submit" disabled={busy} data-testid="report-save-button" className="btn-stamp !bg-forest disabled:opacity-60">
                        {busy ? "Pinning…" : editing ? "Save Changes" : "Pin the Report"}
                    </button>
                    {editing && (
                        <button type="button" onClick={() => { setEditing(null); setForm(EMPTY_REPORT); }} className="btn-stamp !bg-cream !text-ink">
                            Never Mind
                        </button>
                    )}
                </div>
            </form>

            <div className="mt-8 space-y-3">
                {(reports || []).map((r) => (
                    <div key={r.id} data-testid={`report-row-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 border-2 border-ink bg-paper px-5 py-3 hard-shadow">
                        <p className="font-display text-lg tracking-wide text-ink">
                            {r.month_label} {r.published ? <span className="text-forest">· PUBLISHED</span> : <span className="text-ink/50">· DRAFT</span>}
                        </p>
                        <div className="flex gap-2">
                            <button onClick={() => startEdit(r)} data-testid={`report-edit-${r.id}`} className="border-2 border-ink bg-mustard p-1.5 text-ink" aria-label={`Edit ${r.month_label}`}>
                                <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => remove(r.id)} data-testid={`report-delete-${r.id}`} className="border-2 border-ink bg-brick p-1.5 text-cream" aria-label={`Delete ${r.month_label}`}>
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ))}
                {reports && reports.length === 0 && (
                    <p className="border-2 border-dashed border-ink/40 bg-paper p-8 text-center font-hand text-2xl text-ink/60">No reports yet. The ledger is blank and honest.</p>
                )}
            </div>
        </div>
    );
};

const Admin = () => {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("copy");

    if (user === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-forest">
                <p className="font-hand text-3xl text-cream/80">Checking the guest list…</p>
            </div>
        );
    }
    if (user === false) {
        return (
            <>
                <Seo title="Admin" description="PCCH admin." />
                <LoginForm />
            </>
        );
    }

    const tabs = [
        { id: "copy", label: "Page Copy" },
        { id: "studio", label: "Work Room" },
        { id: "volunteers", label: "Volunteers" },
        { id: "submissions", label: "Submissions" },
        { id: "reports", label: "Reports" },
        { id: "payments", label: "Payments" },
    ];

    return (
        <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
            <Seo title="Admin" description="PCCH admin." />
            <header className="border-b-2 border-ink bg-forest px-5 py-4 text-cream sm:px-8">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Spark className="h-7 w-7 text-mustard" />
                        <div>
                            <p className="font-display text-2xl leading-none tracking-wider">CLUBHOUSE OFFICE</p>
                            <p className="text-xs text-cream/70">Signed in as {user.email}</p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <a href="/" data-testid="admin-view-site" className="border-2 border-cream/60 px-4 py-2 font-display text-sm tracking-wider hover:bg-cream hover:text-ink">
                            View Site
                        </a>
                        <button data-testid="admin-logout-button" onClick={logout} className="border-2 border-ink bg-brick px-4 py-2 font-display text-sm tracking-wider text-cream hard-shadow">
                            Log Out
                        </button>
                    </div>
                </div>
            </header>
            <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
                <div className="flex flex-wrap gap-3" role="tablist">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            role="tab"
                            aria-selected={tab === t.id}
                            data-testid={`admin-tab-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`border-2 border-ink px-5 py-2 font-display text-base tracking-wider hard-shadow transition-transform duration-150 hover:-translate-y-0.5 ${
                                tab === t.id ? "bg-orange text-cream" : "bg-paper text-ink"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="mt-8">
                    {tab === "copy" && <CopyTab />}
                    {tab === "studio" && <StudioTab />}
                    {tab === "volunteers" && <VolunteersTab />}
                    {tab === "submissions" && <SubmissionsTab />}
                    {tab === "reports" && <ReportsTab />}
                    {tab === "payments" && <PaymentsTab />}
                </div>
            </div>
        </div>
    );
};

export default Admin;
